import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { isAxiosError, type AxiosError } from "axios";
import { apiClient } from "../zodios/client";
import { handleDefaultMarketplace } from "../utils/handleDefaultMarketplace";
import { isNetworkError } from "../utils/isNetworkError";
import { errorToast } from "../utils/toasts";
import { AMAZON_ONBOARDING_STATUS_QUERY_KEY } from "../modules/onboarding/amazon/useAmazonOnboardingStatus";

type ChangeOrgResult = { status: "orgChanged" } | { status: "orgRejected"; error: AxiosError };

const isRejectedOrg = (error: unknown): error is AxiosError =>
  isAxiosError(error) && (error.response?.status === 400 || error.response?.status === 404);

export const useChangeOrg = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["common"]);

  return useMutation({
    mutationFn: async (orgId: number | string): Promise<ChangeOrgResult> => {
      try {
        await apiClient.changeStaffUserOrg({ org: orgId });
        return { status: "orgChanged" };
      } catch (error) {
        if (isRejectedOrg(error)) {
          return { status: "orgRejected", error };
        }
        throw error;
      }
    },
    onSuccess: async (result) => {
      if (result.status === "orgRejected") {
        errorToast({
          message: t(($) => $.common["No organization found with that id."]),
          error: result.error,
        });
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: AMAZON_ONBOARDING_STATUS_QUERY_KEY,
      });
      await handleDefaultMarketplace();
      window.location.href = `${window.location.origin}/user/dashboard`;
    },
    onError: (error) => {
      errorToast({
        message:
          isNetworkError(error) ?
            t(($) => $.common["Something went wrong. Check your connection and try again."])
          : t(($) => $.common["Something went wrong. Please try again."]),
        error,
      });
    },
  });
};
